/**
 * Pure helpers for the AqiBadge component —
 * extracted for unit testing.
 */

export interface TrendPoint {
  at: string;
  aqi: number | null;
  pm25?: number | null;
}

export interface AqiTrendInput {
  current: { aqi: number | null };
  next8h: TrendPoint[];
}

/**
 * Compute the maximum AQI over the current reading + next-8-hour forecast.
 * Falls back to current AQI (or 0) when forecast points have null AQI.
 */
export function maxAqiNext8h(trend: AqiTrendInput): number {
  return trend.next8h.reduce(
    (m, p) => Math.max(m, p.aqi ?? m),
    trend.current.aqi ?? 0,
  );
}

/**
 * Format an ISO timestamp to a zero-padded hour string, e.g. "08h", "14h".
 */
export function fmtHour(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours().toString().padStart(2, "0")}h`;
}

/**
 * US-EPA AQI banding. Moved here from the old coastal.ts when the maritime
 * panels were removed — air quality is not a coastal concern and outlived them.
 */
export function aqiColor(aqi: number): string {
  if (aqi <= 50) return "var(--good)";
  if (aqi <= 100) return "var(--warn)";
  if (aqi <= 150) return "var(--bad)";
  return "var(--crit)";
}

export function aqiBand(aqi: number): string {
  if (aqi <= 50) return "GOOD";
  if (aqi <= 100) return "MODERATE";
  if (aqi <= 150) return "UNHEALTHY·SG";
  if (aqi <= 200) return "UNHEALTHY";
  return "HAZARDOUS";
}
